import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  deleteEnterpriseProviderKey,
  getEnterpriseProviderKeys,
  setEnterpriseProviderKey,
  type ProviderKeyStatus,
  type SecretProvider,
} from '@/lib/api';

const PROVIDER_LABELS: Partial<Record<SecretProvider, string>> = {
  anthropic: 'Anthropic API key (Claude fallback)',
  openai: 'OpenAI API key (Codex fallback)',
};

const PROVIDER_PLACEHOLDERS: Partial<Record<SecretProvider, string>> = {
  anthropic: 'Enter Anthropic API key',
  openai: 'Enter OpenAI API key',
};

export function EnterpriseApiKeysCard(): React.ReactElement {
  const queryClient = useQueryClient();
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['enterpriseProviderKeys'],
    queryFn: getEnterpriseProviderKeys,
  });

  const saveMutation = useMutation({
    mutationFn: ({ provider, secret }: { provider: SecretProvider; secret: string }) =>
      setEnterpriseProviderKey(provider, secret),
    onSuccess: async (_result, variables) => {
      setSaveError(null);
      setDrafts(prev =>
        Object.fromEntries(Object.entries(prev).filter(([k]) => k !== variables.provider))
      );
      await queryClient.invalidateQueries({ queryKey: ['enterpriseProviderKeys'] });
    },
    onError: (err: Error) => {
      setSaveError(err.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (provider: SecretProvider) => deleteEnterpriseProviderKey(provider),
    onSuccess: async () => {
      setSaveError(null);
      await queryClient.invalidateQueries({ queryKey: ['enterpriseProviderKeys'] });
    },
    onError: (err: Error) => {
      setSaveError(err.message);
    },
  });

  const supported = data?.supported ?? true;
  const keys = useMemo<ProviderKeyStatus[]>(
    () => (data?.keys ?? []).filter(key => PROVIDER_LABELS[key.provider] != null),
    [data]
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Enterprise API Keys</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="mb-3 text-sm text-muted-foreground">
          Enterprise Anthropic API keys are the fallback for Claude when a user has not connected
          Claude OAuth. Enterprise OpenAI keys are the fallback for Codex. Secrets are never echoed
          to the UI after save.
        </p>
        {isLoading && <div className="text-sm text-muted-foreground">Loading…</div>}
        {error instanceof Error && (
          <div className="text-sm text-destructive">
            Failed to load enterprise keys: {error.message}
          </div>
        )}
        {!supported && (
          <div className="mb-3 text-sm text-muted-foreground">
            BYOK is not supported in this environment yet.
          </div>
        )}
        {saveError && <div className="mb-3 text-sm text-destructive">{saveError}</div>}
        <div className="space-y-3">
          {keys.map(key => {
            const draft = drafts[key.provider] ?? '';
            const hasDraft = draft.length > 0;
            const label = PROVIDER_LABELS[key.provider] ?? key.provider;
            return (
              <div
                key={key.provider}
                className="flex flex-col gap-2 rounded-md border border-border p-3"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{label}</span>
                  {key.configured ? (
                    <Badge variant="default">Configured</Badge>
                  ) : (
                    <Badge variant="outline">Not configured</Badge>
                  )}
                </div>
                {key.configured && (
                  <div className="text-xs text-muted-foreground">
                    {key.preview} · updated {key.updatedAt ?? 'unknown'}
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  <Input
                    type="password"
                    placeholder={
                      key.configured
                        ? `Enter new ${label} secret`
                        : (PROVIDER_PLACEHOLDERS[key.provider] ?? 'Enter API key')
                    }
                    value={draft}
                    onChange={e => {
                      setDrafts(prev => ({ ...prev, [key.provider]: e.target.value }));
                    }}
                    disabled={!supported || saveMutation.isPending}
                    className="flex-1 min-w-[200px]"
                  />
                  <Button
                    type="button"
                    onClick={() => {
                      saveMutation.mutate({ provider: key.provider, secret: draft });
                    }}
                    disabled={!supported || !hasDraft || saveMutation.isPending}
                  >
                    Save
                  </Button>
                  {key.configured && (
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={() => {
                        deleteMutation.mutate(key.provider);
                      }}
                      disabled={!supported || deleteMutation.isPending}
                    >
                      Delete
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
